import { ErrorState, InlineMessage, PageContent, PageHeader, Stack } from '@olytic/foundation';
import { UsageCapCard, UsageCapDialog } from '@olytic/foundation/ai-admin';
import { type ReactNode, useState } from 'react';

import { type AiLimitsLoad, capCardProps, LIMITS_NOTE, limitsWarning } from './aiLimitsApi';

// The AI management pages' frame and the Credits & limits page's body, props only
// (AiManagementPages.tsx loads the data), so they render in tests without a sign-in.

/**
 * One AI management page: its header, then the cap warning when usage is near or
 * over this month's cap (none while the limits load or when they fail to), then
 * the page's note.
 */
export function AiPage({ title, note, limits, children }: { title: string; note: ReactNode; limits: AiLimitsLoad; children: ReactNode }): JSX.Element {
  const warning = limitsWarning(limits.state === 'ready' ? limits.body : null);
  return (
    <PageContent as="section" width="wide">
      <Stack>
        <PageHeader
          className="border-0 bg-transparent px-0 pt-0 pb-0"
          eyebrow="AI management"
          title={title}
        />
        {warning ? <InlineMessage tone="warning" role="status">{warning}</InlineMessage> : null}
        <InlineMessage tone="info">{note}</InlineMessage>
        {children}
      </Stack>
    </PageContent>
  );
}

export interface AiCreditsViewProps {
  load: AiLimitsLoad;
  onRetry: () => void;
  /** PUTs the cap (null for "No cap"); resolves once it has finished, saved or not. */
  onSave: (monthlyTokenCap: number | null) => Promise<void>;
  saving: boolean;
  saveError: string | null;
  onClearSaveError: () => void;
}

/**
 * Tokens used this month against the workspace's monthly cap, which only warns
 * (Cameron's card 474109b8). "Set cap" or "Change cap" opens the dialog; saving
 * PUTs the cap and the card shows what the server saved. A failed save closes the
 * dialog, leaves the card as it was and says why; a failed load is an error with Retry.
 */
export function AiCreditsView({ load, onRetry, onSave, saving, saveError, onClearSaveError }: AiCreditsViewProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const body = load.state === 'ready' ? load.body : null;

  return (
    <AiPage title="Credits & limits" note={LIMITS_NOTE} limits={load}>
      {load.state === 'error' ? (
        <ErrorState title="Could not load AI limits" description={load.message} onRetry={onRetry} />
      ) : (
        <>
          {saveError ? <InlineMessage tone="error" role="alert">Could not save the cap. {saveError}</InlineMessage> : null}
          <div className="max-w-md">
            <UsageCapCard
              {...(body ? capCardProps(body) : { used: 0, cap: null })}
              loading={body === null}
              onSetCap={() => {
                onClearSaveError();
                setEditing(true);
              }}
            />
          </div>
          {body ? (
            <UsageCapDialog
              open={editing}
              onOpenChange={(open) => !open && setEditing(false)}
              value={body.cap}
              used={body.used}
              busy={saving}
              onSave={(next) => {
                void onSave(next).then(() => setEditing(false));
              }}
            />
          ) : null}
        </>
      )}
    </AiPage>
  );
}
